import { useEffect, useRef } from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';

export type tSeatCardCaptureProps = {
  /** Controlled: opened proactively by the lookup, or after a decline by the customer. */
  isOpen: boolean;
};

/**
 * Card entry, inline, inside the add-teammate form.
 *
 * The people without a usable card are precisely the ones this project exists to stop
 * dead-ending, so the card is collected here rather than behind a link to Payment
 * Methods — leaving the tab is the two-trip errand the whole change removes.
 *
 * It sits directly beneath the cost notice and above the submit control, inside the
 * same `<form>`, as an inline reveal. None of the existing payment components
 * demonstrate that: `AddPaymentMethod`, `PaymentMethodsManager` and the ManageUsers
 * modal all open a nested modal. Their Stripe wiring is borrowed; their container is
 * deliberately not.
 *
 * Controlled rather than self-opening, because it has two triggers — the proactive
 * lookup and "use a different card" after a decline — and two implementations of the
 * same block would drift.
 */
export default function SeatCardCapture(props: tSeatCardCaptureProps) {
  const { t } = useTranslation('settings/team');
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Focus follows the reveal: a field that appears mid-form without focus is easy to
  // miss, and this one is between the customer and the thing they came to do.
  useEffect(() => {
    if (!props.isOpen) return;
    const firstField = containerRef.current?.querySelector<HTMLElement>(
      'iframe, input, [tabindex]:not([tabindex="-1"])',
    );
    firstField?.focus();
  }, [props.isOpen]);

  if (!props.isOpen) return null;

  return (
    <section
      ref={containerRef}
      className="TeamSection__Modal__CardCapture"
      data-testid="seat-card-capture"
      aria-live="polite"
    >
      <p className="TeamSection__Modal__CardCapture__title">
        {t('page.seatCost.card.title')}
      </p>
      <PaymentElement />
    </section>
  );
}
